import Swal from "sweetalert2";

/**
 * Errores de backend al guardar nota de crédito ligada a shipping.
 * @param {unknown} error
 * @returns {string}
 */
function errorMessage(error) {
  return String(
    (error && error.response && error.response.data && (error.response.data.message || error.response.data.error)) ||
      (error && error.message) ||
      ""
  );
}

/**
 * @param {unknown} error
 * @returns {boolean}
 */
export function isCreditOnFullyCollectedShippingError(error) {
  const msg = errorMessage(error);
  return (
    msg.includes("NOTA_COMERCIAL_CREDIT_ON_FULLY_COLLECTED_SHIPPING") ||
    /nota de cr[eé]dito.*ya se cobr/i.test(msg) ||
    /credit note.*already collected/i.test(msg)
  );
}

/**
 * @param {unknown} error
 * @returns {boolean}
 */
export function isCreditExceedsShippingPendingError(error) {
  const msg = errorMessage(error);
  return (
    msg.includes("NOTA_COMERCIAL_CREDIT_EXCEEDS_SHIPPING_PENDING") ||
    /supera lo pendiente/i.test(msg) ||
    /exceeds.*pending/i.test(msg)
  );
}

/**
 * Contacto de la nota que no participa de la operación a la que se la aplica.
 * @param {unknown} error
 * @returns {boolean}
 */
export function isContactNotInOperationError(error) {
  const msg = errorMessage(error);
  return (
    msg.includes("NOTA_COMERCIAL_CONTACT_NOT_IN_OPERATION") ||
    /contacto no participa/i.test(msg) ||
    /contact does not take part/i.test(msg)
  );
}

/**
 * Moneda de la nota distinta a la del gasto al que se aplica.
 * @param {unknown} error
 * @returns {boolean}
 */
export function isCurrencyMismatchError(error) {
  const msg = errorMessage(error);
  return (
    msg.includes("NOTA_COMERCIAL_CURRENCY_MISMATCH") ||
    /moneda distinta/i.test(msg) ||
    /different currency/i.test(msg)
  );
}

/** Claves i18n del aviso por tipo de error, para no repetir la cadena de ternarios. */
const SAVE_ERROR_I18N_KEYS = {
  exceedsPending: {
    title: "notaComercial.noteForm.creditExceedsShippingPendingTitle",
    text: "notaComercial.noteForm.creditExceedsShippingPending",
  },
  fullyCollected: {
    title: "notaComercial.noteForm.creditOnFullyCollectedShippingTitle",
    text: "notaComercial.noteForm.creditOnFullyCollectedShipping",
  },
  contactNotInOperation: {
    title: "notaComercial.noteForm.contactNotInOperationTitle",
    text: "notaComercial.noteForm.contactNotInOperation",
  },
  currencyMismatch: {
    title: "notaComercial.noteForm.currencyMismatchTitle",
    text: "notaComercial.noteForm.currencyMismatch",
  },
};

/**
 * Claves de título y texto del aviso, o null si el error no es uno de los conocidos.
 * @param {unknown} error
 * @returns {{ title: string, text: string }|null}
 */
export function creditNoteShippingSaveErrorKeys(error) {
  const kind = creditNoteShippingSaveErrorKind(error);
  return kind ? SAVE_ERROR_I18N_KEYS[kind] : null;
}

/**
 * @param {unknown} error
 * @returns {'fullyCollected'|'exceedsPending'|'contactNotInOperation'|null}
 */
export function creditNoteShippingSaveErrorKind(error) {
  if (isContactNotInOperationError(error)) return "contactNotInOperation";
  if (isCurrencyMismatchError(error)) return "currencyMismatch";
  if (isCreditExceedsShippingPendingError(error)) return "exceedsPending";
  if (isCreditOnFullyCollectedShippingError(error)) return "fullyCollected";
  return null;
}

/**
 * Aviso de nota recibida sin adjunto, con la opción de guardar igual.
 *
 * Mismo trato que el cobro sin comprobante: el archivo es el respaldo del documento
 * que nos dieron, pero no todas las notas llegan con él en el momento.
 *
 * @param {(key: string, params?: object) => string} t
 * @returns {Promise<boolean>} true si el usuario decide guardar igual
 */
export async function promptNotaComercialMissingFile(t) {
  const result = await Swal.fire({
    icon: "warning",
    title: t("notaComercial.noteForm.missingFileTitle"),
    text: t("notaComercial.noteForm.missingFileText"),
    showCancelButton: true,
    confirmButtonText: t("validationSaveAnyway"),
    cancelButtonText: t("notaComercial.noteForm.missingFileCancel"),
    confirmButtonColor: "#28a745",
    cancelButtonColor: "#dc3545",
    heightAuto: false,
  });
  return !!result.isConfirmed;
}

/**
 * Aviso de moneda distinta a la del gasto. Sin botón de guardar igual: no hay conversión,
 * así que aplicar la nota contra otro importe daría un saldo mal sin que nada lo avise.
 *
 * @param {(key: string, params?: object) => string} t
 * @returns {Promise<void>}
 */
export async function notaComercialCurrencyMismatchAlert(t) {
  await Swal.fire({
    icon: "warning",
    title: t("notaComercial.noteForm.currencyMismatchTitle"),
    text: t("notaComercial.noteForm.currencyMismatch"),
    heightAuto: false,
  });
}
